import {
  AspectRatio,
  Avatar,
  Box,
  Button,
  Center,
  CircularProgress,
  Fade,
  Flex,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Radio,
  RadioGroup,
  SimpleGrid,
  Spinner,
  Text,
  useMediaQuery,
  VStack,
  HStack,
  IconButton,
  Badge,
  Card,
  CardBody,
  Divider,
  useColorModeValue,
  useToast,
  Tooltip,
} from "@chakra-ui/react";
import MainLayout from "@layouts/main.layout";
import AddItem from "components/AddItem";
import FAIcon from "components/FAIcon";
import VideoSelector from "components/VideoSelector";
import useAPI from "hooks/api";
import getPublicDownloadUrl from "libs/get-public-download-url";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { ref, uploadBytes } from "firebase/storage";
import { useFirebase } from "context/firebase.context";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const DraggableItem = ({
  type,
  picture,
  displayName,
  thumbnails,
  thumbnail,
  onRemove,
  index,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging,
  isDropTarget,
}) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const dragOverBg = useColorModeValue("blue.50", "blue.800");
  
  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
    onDragStart(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    onDragOver(index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"));
    onDrop(draggedIndex, index);
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  return (
    <Box
      mt={4}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      opacity={isDragging ? 0.5 : 1}
      transform={isDragging ? "rotate(5deg)" : "none"}
      transition="all 0.2s"
      cursor="grab"
      _active={{ cursor: "grabbing" }}
      position="relative"
    >
      {/* Drop indicator */}
      {isDropTarget && (
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          border="3px dashed"
          borderColor="blue.400"
          borderRadius="lg"
          bg={dragOverBg}
          opacity={0.7}
          zIndex={1}
        />
      )}

      {type === "creators" ? (
        <Card
          bg={bgColor}
          borderColor={borderColor}
          borderWidth="2px"
          shadow="lg"
          transition="all 0.3s"
          _hover={{ 
            shadow: "xl", 
            transform: "translateY(-4px) scale(1.02)",
            borderColor: "blue.300"
          }}
          borderRadius="xl"
        >
          <CardBody p={6}>
            <VStack spacing={4}>
              <Box position="relative">
                <Avatar 
                  src={getPublicDownloadUrl(picture)} 
                  size="xl" 
                  border="4px solid"
                  borderColor="blue.400"
                  shadow="md"
                />
                <Tooltip label="移除項目" placement="top">
                  <IconButton
                    icon={<FAIcon name="times" />}
                    size="sm"
                    colorScheme="red"
                    variant="solid"
                    isRound
                    position="absolute"
                    top={-2}
                    right={-2}
                    zIndex={10}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                    shadow="lg"
                    _hover={{ transform: "scale(1.1)" }}
                  />
                </Tooltip>
              </Box>
              <Text 
                align="center" 
                fontWeight="bold" 
                fontSize="sm"
                color={textColor}
                noOfLines={2}
                lineHeight="shorter"
              >
                {displayName}
              </Text>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <Card
          bg={bgColor}
          borderColor={borderColor}
          borderWidth="2px"
          shadow="lg"
          transition="all 0.3s"
          _hover={{ 
            shadow: "xl", 
            transform: "translateY(-4px) scale(1.02)",
            borderColor: "blue.300"
          }}
          borderRadius="xl"
        >
          <Box width={90} position="relative">
            <AspectRatio ratio={9 / 16} borderRadius="lg" overflow="hidden">
              <Image 
                src={thumbnail || thumbnails} 
                objectFit="cover"
                transition="transform 0.3s"
                _hover={{ transform: "scale(1.05)" }}
              />
            </AspectRatio>
            <Tooltip label="移除項目" placement="top">
              <IconButton
                icon={<FAIcon name="times" />}
                size="sm"
                colorScheme="red"
                variant="solid"
                isRound
                position="absolute"
                top={-2}
                right={-2}
                zIndex={10}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                shadow="lg"
                _hover={{ transform: "scale(1.1)" }}
              />
            </Tooltip>
          </Box>
        </Card>
      )}

      {/* Drag handle indicator */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        opacity={0}
        _hover={{ opacity: 1 }}
        transition="opacity 0.2s"
        pointerEvents="none"
        bg="blackAlpha.600"
        color="white"
        px={3}
        py={1}
        borderRadius="md"
        fontSize="xs"
        fontWeight="bold"
      >
        <FAIcon name="arrows-alt" mr={2} />
        拖移
      </Box>
    </Box>
  );
};

const CreatorSelector = ({ creators, onSelect }) => {
  const bgColor = useColorModeValue("gray.50", "gray.700");
  const hoverBg = useColorModeValue("blue.50", "blue.600");
  
  return (
    <SimpleGrid columns={3} gap={6} p={8}>
      {creators.map((creator) => (
        <Card
          key={`selector-${creator.id}`}
          role="button"
          onClick={() => onSelect(creator)}
          cursor="pointer"
          bg={bgColor}
          _hover={{ 
            bg: hoverBg, 
            transform: "scale(1.05)",
            shadow: "lg"
          }}
          transition="all 0.2s"
          shadow="md"
          _active={{ transform: "scale(0.98)" }}
          borderRadius="xl"
        >
          <CardBody p={6}>
            <VStack spacing={4}>
              <Avatar 
                src={getPublicDownloadUrl(creator.picture)} 
                size="lg"
                border="3px solid"
                borderColor="blue.400"
              />
              <Text fontSize="sm" fontWeight="semibold" textAlign="center" noOfLines={2}>
                {creator.displayName}
              </Text>
            </VStack>
          </CardBody>
        </Card>
      ))}
    </SimpleGrid>
  );
};

const Group = ({
  name,
  type,
  items,
  loading,
  onLoad,
  onRemove,
  onMove,
  onAddItem,
  onSave,
  groupIndex,
  onRegisterAddCallback, // 新增：註冊新增項目回調
}) => {
  const [buffer, setBuffer] = useState(name || "");
  const [localItems, setLocalItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]); // 保存原始載入的資料
  const [originalName, setOriginalName] = useState(name || ""); // 保存原始名稱
  const [priceAsc, setPriceAsc] = useState(false);
  const [timeAsc, setTimeAsc] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false); // 新增：是否已載入資料
  const [shouldUpdateOriginal, setShouldUpdateOriginal] = useState(false); // 是否應該更新原始狀態

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const inputBg = useColorModeValue("gray.50", "gray.700");
  const inputFocusBg = useColorModeValue("white", "gray.600");
  const addItemBlueBg = useColorModeValue("blue.50", "blue.900");
  const headerBg = useColorModeValue("gray.50", "gray.700");
  const toast = useToast();

  const isCreator = type === "creators";

  // 處理載入按鈕點擊
  const handleLoad = useCallback(async () => {
    setShouldUpdateOriginal(true); // 標記需要更新原始狀態
    await onLoad();
    setHasLoaded(true);
    toast({
      title: "資料已載入",
      description: "現在可以進行編輯操作",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  }, [onLoad, toast]);

  // 同步外部 items 到本地狀態
  useEffect(() => {
    if (items && hasLoaded) {
      setLocalItems([...items]);
      
      // 只有當標記為 true 時才更新原始狀態（即載入按鈕被點擊後）
      if (shouldUpdateOriginal) {
        setOriginalItems([...items]);
        setOriginalName(name || "");
        setHasChanges(false);
        setShouldUpdateOriginal(false); // 重置標記
      }
    }
  }, [items, hasLoaded, name, shouldUpdateOriginal]);

  // 檢查是否有變更
  useEffect(() => {
    if (!hasLoaded) return;
    const hasItemChanges = JSON.stringify(localItems.map(item => item.id)) !== JSON.stringify(originalItems.map(item => item.id));
    const hasNameChanges = buffer !== originalName;
    setHasChanges(hasItemChanges || hasNameChanges);
  }, [localItems, originalItems, buffer, originalName, hasLoaded]);

  const handleDragStart = useCallback((index) => {
    if (!hasLoaded) {
      toast({
        title: "請先載入資料",
        description: "需要載入最新資料後才能進行排序",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setDraggedIndex(index);
  }, [hasLoaded, toast]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDropTargetIndex(null);
  }, []);

  const handleDragOver = useCallback((index) => {
    setDropTargetIndex(index);
  }, []);

  const handleDrop = useCallback((draggedIdx, targetIdx) => {
    if (!hasLoaded) return;
    if (draggedIdx === targetIdx) return;
    
    const newItems = [...localItems];
    const draggedItem = newItems[draggedIdx];
    newItems.splice(draggedIdx, 1);
    newItems.splice(targetIdx, 0, draggedItem);
    
    setLocalItems(newItems);
    setDraggedIndex(null);
    setDropTargetIndex(null);
    
    toast({
      title: "項目已移動",
      description: "請點擊保存按鈕來確認變更",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  }, [localItems, hasLoaded, toast]);

  const onSortPrice = useCallback(() => {
    if (!hasLoaded) {
      toast({
        title: "請先載入資料",
        description: "需要載入最新資料後才能進行排序",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // 先切換狀態
    const newPriceAsc = !priceAsc;
    setPriceAsc(newPriceAsc);
    
    // 根據新狀態排序，並處理數據安全性
    const sorted = [...localItems].sort((a, b) => {
      const priceA = Number(a.price) || 0;
      const priceB = Number(b.price) || 0;
      return newPriceAsc ? priceA - priceB : priceB - priceA;
    });
    
    setLocalItems(sorted);
    
    // 提供排序順序提示
    toast({
      title: "價格排序完成",
      description: `已按價格${newPriceAsc ? "低→高" : "高→低"}排序`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  }, [localItems, priceAsc, hasLoaded, toast]);
  
  const onSortTime = useCallback(() => {
    if (!hasLoaded) {
      toast({
        title: "請先載入資料",
        description: "需要載入最新資料後才能進行排序",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // 先切換狀態
    const newTimeAsc = !timeAsc;
    setTimeAsc(newTimeAsc);
    
    // 根據新狀態排序，並處理時間格式
    const sorted = [...localItems].sort((a, b) => {
      const timeA = new Date(a.createdAt).getTime() || 0;
      const timeB = new Date(b.createdAt).getTime() || 0;
      return newTimeAsc ? timeA - timeB : timeB - timeA;
    });
    
    setLocalItems(sorted);
    
    // 提供排序順序提示
    toast({
      title: "時間排序完成",
      description: `已按時間${newTimeAsc ? "舊→新" : "新→舊"}排序`,
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  }, [localItems, timeAsc, hasLoaded, toast]);

  const handleRemoveItem = useCallback((index) => {
    if (!hasLoaded) {
      toast({
        title: "請先載入資料",
        description: "需要載入最新資料後才能移除項目",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const newItems = [...localItems];
    newItems.splice(index, 1);
    setLocalItems(newItems);
  }, [localItems, hasLoaded, toast]);

  const handleAddItem = useCallback(() => {
    if (!hasLoaded) {
      toast({
        title: "請先載入資料",
        description: "需要載入最新資料後才能新增項目",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onAddItem();
  }, [hasLoaded, onAddItem, toast]);

  // 註冊新增項目的回調函數
  useEffect(() => {
    if (onRegisterAddCallback) {
      const addItemToLocal = (newItem) => {
        setLocalItems(prevItems => [newItem, ...prevItems]); // 新項目添加到前面
      };
      onRegisterAddCallback(groupIndex, addItemToLocal);
      
      // 清理函數
      return () => {
        onRegisterAddCallback(groupIndex, null);
      };
    }
  }, [groupIndex, onRegisterAddCallback]);

  const handleSave = useCallback(async () => {
    try {
      await onSave(groupIndex, {
        name: buffer,
        items: localItems
      });
      
      toast({
        title: "保存成功",
        description: "群組設定已更新，請重新載入資料以繼續編輯",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      
      // 保存後重置狀態
      setHasChanges(false);
      setHasLoaded(false); // 重置載入狀態
      setLocalItems([]); // 清空本地項目
      setOriginalItems([]); // 清空原始項目
      setBuffer(name || ""); // 重置名稱
      setOriginalName(name || ""); // 重置原始名稱
      setShouldUpdateOriginal(false); // 重置更新標記
      
    } catch (error) {
      toast({
        title: "保存失敗",
        description: "請稍後再試",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [buffer, localItems, onSave, groupIndex, toast, name]);

  const handleReset = useCallback(() => {
    setBuffer(originalName);
    if (hasLoaded) {
      setLocalItems([...originalItems]);
    } else {
      setLocalItems([]);
    }
    setHasChanges(false);
  }, [originalName, originalItems, hasLoaded]);

  const handleNameChange = useCallback((e) => {
    if (!hasLoaded) {
      toast({
        title: "請先載入資料",
        description: "需要載入最新資料後才能編輯群組名稱",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setBuffer(e.target.value);
  }, [hasLoaded, toast]);

  return (
    <Card 
      mb={8} 
      maxW={1200} 
      bg={bgColor}
      borderColor={hasChanges ? "orange.300" : (hasLoaded ? borderColor : "red.300")}
      borderWidth="2px"
      shadow={hasChanges ? "xl" : "lg"}
      rounded="2xl"
      transition="all 0.3s"
      position="relative"
    >
      {/* 狀態指示器 */}
      {!hasLoaded && (
        <Box
          position="absolute"
          top={-2}
          left={-2}
          bg="red.400"
          color="white"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
          fontWeight="bold"
          shadow="md"
          zIndex={2}
        >
          需要載入資料
        </Box>
      )}
      
      {hasChanges && hasLoaded && (
        <Box
          position="absolute"
          top={-2}
          right={-2}
          bg="orange.400"
          color="white"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
          fontWeight="bold"
          shadow="md"
          zIndex={2}
        >
          有未保存的變更
        </Box>
      )}

      <CardBody p={0}>
        {/* Header */}
        <Box bg={headerBg} p={6} borderTopRadius="2xl">
          <Flex align="center" gap={4} mb={4}>
            <VStack spacing={1}>
              <Tooltip label="向上移動群組" placement="top">
                <IconButton
                  icon={<FAIcon name="chevron-up" />}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={onMove(-1)}
                />
              </Tooltip>
              <Tooltip label="向下移動群組" placement="top">
                <IconButton
                  icon={<FAIcon name="chevron-down" />}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={onMove(1)}
                />
              </Tooltip>
            </VStack>
            
            <Input
              value={buffer}
              onChange={handleNameChange}
              maxW={250}
              size="lg"
              fontWeight="bold"
              fontSize="lg"
              bg={inputBg}
              border="2px solid transparent"
              _focus={{
                borderColor: "blue.400",
                bg: inputFocusBg,
              }}
              placeholder="輸入群組名稱"
              isDisabled={!hasLoaded}
              opacity={hasLoaded ? 1 : 0.6}
            />
            
            <Badge 
              colorScheme={isCreator ? "purple" : "orange"} 
              px={4} 
              py={2} 
              rounded="full"
              fontWeight="bold"
              fontSize="sm"
            >
              {isCreator ? "創作者" : "影片"} ({hasLoaded ? localItems.length : '?'})
            </Badge>
            
            <Box flex={1} />

            {/* Reload 按鈕 */}
            <Tooltip label={hasLoaded ? "重新載入最新資料" : "載入資料（必須）"} placement="top">
              <IconButton
                icon={<FAIcon name="sync-alt" />}
                onClick={handleLoad}
                colorScheme={hasLoaded ? "blue" : "red"}
                variant={hasLoaded ? "outline" : "solid"}
                size="sm"
                isLoading={loading}
                _hover={{ transform: "rotate(180deg)" }}
                transition="transform 0.3s"
              />
            </Tooltip>

            {/* 保存和重置按鈕 */}
            <HStack spacing={2}>
              <Tooltip label="重置變更" placement="top">
                <IconButton
                  icon={<FAIcon name="undo" />}
                  onClick={handleReset}
                  isDisabled={!hasChanges || !hasLoaded}
                  colorScheme="gray"
                  variant="outline"
                  size="sm"
                />
              </Tooltip>
              <Button
                onClick={handleSave}
                isDisabled={!hasChanges || !hasLoaded}
                colorScheme="green"
                variant="solid"
                size="sm"
                leftIcon={<FAIcon name="save" />}
                shadow="md"
                _hover={{ transform: "translateY(-1px)" }}
              >
                保存變更
              </Button>
            </HStack>
            
            {type === "videos" && (
              <HStack spacing={2}>
                <Tooltip label={`按價格排序 (目前: ${priceAsc ? "低→高" : "高→低"})`} placement="top">
                  <Button 
                    onClick={onSortPrice} 
                    isDisabled={loading || !localItems.length || !hasLoaded}
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    leftIcon={<FAIcon name={priceAsc ? "sort-numeric-up" : "sort-numeric-down"} />}
                    opacity={hasLoaded ? 1 : 0.6}
                  >
                    價格 {priceAsc ? "低→高" : "高→低"}
                  </Button>
                </Tooltip>
                <Tooltip label={`按時間排序 (目前: ${timeAsc ? "舊→新" : "新→舊"})`} placement="top">
                  <Button 
                    onClick={onSortTime} 
                    isDisabled={loading || !localItems.length || !hasLoaded}
                    size="sm"
                    variant="outline"
                    colorScheme="green"
                    leftIcon={<FAIcon name={timeAsc ? "sort-numeric-up" : "sort-numeric-down"} />}
                    opacity={hasLoaded ? 1 : 0.6}
                  >
                    時間 {timeAsc ? "舊→新" : "新→舊"}
                  </Button>
                </Tooltip>
              </HStack>
            )}

            <Tooltip label="刪除群組" placement="top">
              <IconButton
                icon={<FAIcon name="trash-alt" />}
                onClick={onRemove}
                colorScheme="red"
                variant="ghost"
                size="sm"
              />
            </Tooltip>
          </Flex>
        </Box>

        <Divider />

        {/* Content */}
        <Box p={6}>
          <Flex gap={6} align="center" overflowX="auto" width="100%" pb={4}>
            {loading ? (
              <Center flex={1} py={16}>
                <VStack spacing={4}>
                  <Spinner size="xl" color="blue.400" thickness="4px" />
                  <Text color="gray.500" fontSize="lg">載入中...</Text>
                </VStack>
              </Center>
            ) : !hasLoaded ? (
              <Center flex={1} py={16}>
                <VStack spacing={6}>
                  <FAIcon name="download" fontSize="4xl" color="red.400" />
                  <VStack spacing={2}>
                    <Text color="red.500" fontSize="xl" fontWeight="bold">需要載入資料</Text>
                    <Text color="gray.500" fontSize="md" textAlign="center">
                      請點擊右上角的載入按鈕<br />獲取最新資料後才能進行編輯操作
                    </Text>
                  </VStack>
                </VStack>
              </Center>
            ) : localItems.length === 0 ? (
              <>
                <Tooltip label={hasLoaded ? "新增項目" : "請先載入資料"} placement="top">
                  <Box>
                    <AddItem
                      onClick={handleAddItem}
                      ratio={isCreator ? null : 9 / 16}
                      width={isCreator ? 32 : 90}
                      height={isCreator ? 36 : null}
                      minW={isCreator ? null : 90}
                      rounded="xl"
                      border="3px dashed"
                      borderColor={hasLoaded ? "gray.300" : "red.300"}
                      _hover={hasLoaded ? {
                        borderColor: "blue.400",
                        bg: addItemBlueBg,
                        transform: "scale(1.05)",
                      } : {}}
                      transition="all 0.2s"
                      bg={hasLoaded ? "gray.50" : "red.50"}
                      opacity={hasLoaded ? 1 : 0.6}
                      cursor={hasLoaded ? "pointer" : "not-allowed"}
                    />
                  </Box>
                </Tooltip>
                <Center flex={1} py={8}>
                  <VStack spacing={4}>
                    <FAIcon name="inbox" fontSize="3xl" color="gray.300" />
                    <VStack spacing={1}>
                      <Text color="gray.500" fontSize="md" fontWeight="medium">此群組暫無內容</Text>
                      <Text color="gray.400" fontSize="sm">點擊左側「新增項目」添加內容</Text>
                    </VStack>
                  </VStack>
                </Center>
              </>
            ) : (
              <>
                <Tooltip label="新增項目" placement="top">
                  <Box>
                    <AddItem
                      onClick={handleAddItem}
                      ratio={isCreator ? null : 9 / 16}
                      width={isCreator ? 32 : 90}
                      height={isCreator ? 36 : null}
                      minW={isCreator ? null : 90}
                      rounded="xl"
                      border="3px dashed"
                      borderColor="gray.300"
                      _hover={{
                        borderColor: "blue.400",
                        bg: addItemBlueBg,
                        transform: "scale(1.05)",
                      }}
                      transition="all 0.2s"
                      bg="gray.600"
                    />
                  </Box>
                </Tooltip>
                {localItems.map((item, index) => (
                  <DraggableItem
                    key={`group-${groupIndex}-item-${index}-${item.id || index}`}
                    type={type}
                    index={index}
                    onRemove={() => handleRemoveItem(index)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    isDragging={draggedIndex === index}
                    isDropTarget={dropTargetIndex === index && draggedIndex !== index}
                    {...item}
                  />
                ))}
              </>
            )}
          </Flex>
        </Box>
      </CardBody>
    </Card>
  );
};

const AddBanner = ({ onAdd }) => {
  const fileRef = useRef(null);
  const [draft, setDraft] = useState(null);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const uploadAreaBg = useColorModeValue("gray.50", "gray.700");
  const bannerBlueBg = useColorModeValue("blue.50", "blue.900");
  const bannerBlueHoverBg = useColorModeValue("blue.100", "blue.800");

  const createDraft = useCallback(() => setDraft({ url: "", file: null }), []);

  const updateDraftImage = useCallback((e) => {
    const attachment = e.target.files[0];
    setDraft((state) => ({ ...state, file: attachment }));
  }, []);

  const image = useMemo(() => {
    if (!draft?.file) return null;
    return URL.createObjectURL(draft.file);
  }, [draft]);

  const addBanner = useCallback(async () => {
    if (!draft || !draft.file) return;

    await onAdd(draft);
    setDraft(null);
  }, [draft, onAdd]);

  return (
    <>
      <Modal isOpen={!!draft} onClose={() => setDraft(null)} size="lg">
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg={bgColor} rounded="2xl" shadow="2xl" maxW="600px">
          <ModalBody p={0}>
            {draft && (
              <Box p={8}>
                <Text fontSize="3xl" fontWeight="bold" mb={8} textAlign="center" color="blue.600">
                  新增橫幅
                </Text>
                
                {image ? (
                  <Box mb={8} rounded="xl" overflow="hidden" shadow="xl" border="3px solid" borderColor="blue.200">
                    <Image src={image} w="100%" maxH="300px" objectFit="cover" />
                  </Box>
                ) : (
                  <Center 
                    h={64} 
                    mb={8} 
                    border="3px dashed" 
                    borderColor="blue.300" 
                    rounded="xl"
                    bg={uploadAreaBg}
                    transition="all 0.2s"
                    _hover={{
                      borderColor: "blue.400",
                      bg: "blue.50"
                    }}
                  >
                    <VStack spacing={4}>
                      <FAIcon name="cloud-upload-alt" fontSize="4xl" color="blue.400" />
                      <Button 
                        onClick={() => fileRef.current.click()} 
                        colorScheme="blue"
                        size="lg"
                        leftIcon={<FAIcon name="plus" />}
                        shadow="lg"
                      >
                        選擇圖片檔案
                      </Button>
                      <Text color="gray.500" fontSize="sm">支援 JPG, PNG 格式</Text>
                    </VStack>
                  </Center>
                )}
                
                <Input
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  hidden
                  onChange={updateDraftImage}
                />
                
                <InputGroup mb={8} size="lg">
                  <InputLeftAddon bg="blue.100" color="blue.700" fontWeight="bold" borderColor="blue.200">
                    <FAIcon name="link" mr={2} />
                    連結網址
                  </InputLeftAddon>
                  <Input
                    value={draft.url}
                    onChange={(e) =>
                      setDraft((state) => ({
                        ...state,
                        url: e.target.value,
                      }))
                    }
                    placeholder="輸入點擊橫幅後的跳轉網址"
                    borderColor="blue.200"
                    _focus={{ borderColor: "blue.400" }}
                  />
                </InputGroup>

                <HStack spacing={4}>
                  <Button
                    onClick={() => setDraft(null)}
                    variant="outline"
                    colorScheme="gray"
                    flex={1}
                    size="lg"
                  >
                    取消
                  </Button>
                  <Button
                    onClick={addBanner}
                    colorScheme="blue"
                    flex={2}
                    size="lg"
                    isDisabled={!image}
                    leftIcon={<FAIcon name="plus" />}
                    shadow="lg"
                    _hover={{ transform: "translateY(-2px)" }}
                  >
                    新增橫幅
                  </Button>
                </HStack>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Box
        as="button"
        onClick={createDraft}
        w="100%"
        h="200px"
        border="4px dashed"
        borderColor="blue.300"
        bg={bannerBlueBg}
        _hover={{
          borderColor: "blue.500",
          bg: bannerBlueHoverBg,
          transform: "scale(1.01)",
        }}
        transition="all 0.3s"
        rounded="2xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        shadow="lg"
      >
        <VStack spacing={4}>
          <FAIcon name="plus-circle" fontSize="4xl" color="blue.400" />
          <Text fontSize="xl" fontWeight="bold" color="blue.600">
            新增橫幅
          </Text>
        </VStack>
      </Box>
    </>
  );
};

export default function ExplorePage() {
  const { storage } = useFirebase();
  const [isDesktop] = useMediaQuery("(min-width: 992px)");
  const api = useAPI();
  const [settings, setSettings] = useState(null);
  const [creators, setCreators] = useState(null);
  const [draftGroup, setDraftGroup] = useState(null);
  const [editing, setEditing] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("IDLE");

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const addGroupBlueBg = useColorModeValue("blue.50", "blue.900");
  const addGroupBlueHoverBg = useColorModeValue("blue.100", "blue.800");
  const toast = useToast();

  const editingGroup = useMemo(
    () => settings?.groups?.[editing] || {},
    [editing, settings?.groups],
  );

  const openGroupEditModal = useCallback(
    (index) => () => setEditing(index),
    [],
  );
  const closeEditModal = useCallback(() => setEditing(null), []);

  const createDraftGroup = useCallback(
    () => setDraftGroup({ name: "", type: "creators", tags: [] }),
    [],
  );

  const apiCall = useCallback(
    (method) =>
      async (...params) => {
        setUpdateStatus("UPDATING");
        try {
          const data = await api[method](...params);
          setUpdateStatus("DONE");
          setTimeout(() => setUpdateStatus("IDLE"), 1500);
          return data;
        } catch (error) {
          setUpdateStatus("ERROR");
          setTimeout(() => setUpdateStatus("IDLE"), 3000);
          throw error;
        }
      },
    [api],
  );

  const loadGroup = useCallback(
    (index) => async () => {
      let clone = cloneDeep(settings);
      clone.groups[index].loading = true;
      setSettings(clone);
      
      try {
        const { result } = await api.getExploreSettingsByChunk(index, 0, 99999);
        clone = cloneDeep(settings);
        delete clone.groups[index].loading;
        clone.groups[index].items = result.items;
        setSettings(clone);
        
        // 顯示載入成功的提示
        toast({
          title: "載入成功",
          description: "群組資料已更新",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        clone = cloneDeep(settings);
        delete clone.groups[index].loading;
        setSettings(clone);
        toast({
          title: "載入失敗",
          description: "無法載入群組內容",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [api, settings, toast],
  );

  const handleGroupSave = useCallback(async (groupIndex, { name, items }) => {
    const operations = [];
    
    // 更新名稱 (如果有變更)
    if (name !== settings.groups[groupIndex].name) {
      operations.push(
        apiCall("updateExploreSettingsGroup")(groupIndex, {
          type: "update_name",
          payload: { name },
        })
      );
    }

    // 統一使用 arrange_items 來處理所有項目變更（新增、移除、排序）
    operations.push(
      apiCall("updateExploreSettingsGroup")(groupIndex, {
        type: "arrange_items",
        payload: { items: items.map((item) => item.id) },
      })
    );

    await Promise.all(operations);

    // 更新本地狀態
    const clone = cloneDeep(settings);
    clone.groups[groupIndex].name = name;
    clone.groups[groupIndex].items = items;
    setSettings(clone);
  }, [apiCall, settings]);

  const addGroup = useCallback(async () => {
    const data = await apiCall("createExploreSettingsGroup")(draftGroup);

    setSettings((state) => ({
      ...state,
      groups: state.groups.concat(data),
    }));
    setDraftGroup(null);
  }, [apiCall, draftGroup]);

  const moveGroup = useCallback(
    (index) => (vector) => async () => {
      const clone = cloneDeep(settings);
      const target = index + vector;
      if (target < 0 || target >= clone.groups.length) return;
      await apiCall("swapExploreSettingsGroup")(index, target);
      const temp = clone.groups[index];
      clone.groups[index] = clone.groups[target];
      clone.groups[target] = temp;
      setSettings(clone);
    },
    [apiCall, settings],
  );

  const removeGroup = useCallback(
    (index) => async () => {
      await apiCall("removeExploreSettingsGroup")(index);

      const clone = cloneDeep(settings);
      clone.groups.splice(index, 1);

      setSettings(clone);
    },
    [apiCall, settings],
  );



  // 用於保存每個群組的新增項目回調
  const [addItemCallbacks, setAddItemCallbacks] = useState({});

  // 註冊群組的新增項目回調
  const registerAddCallback = useCallback((groupIndex, callback) => {
    setAddItemCallbacks(prev => ({
      ...prev,
      [groupIndex]: callback
    }));
  }, []);

  const addItem = useCallback(
    async (newItem) => {
      // 使用對應群組的回調函數直接新增到本地狀態
      if (addItemCallbacks[editing]) {
        addItemCallbacks[editing](newItem);
      }
      
      setEditing(null);
      
      toast({
        title: "項目已新增",
        description: "請記得點擊保存按鈕來確認變更",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    },
    [editing, addItemCallbacks, toast],
  );

  const addBanner = useCallback(
    async (draft) => {
      const attachment = draft.file;
      const fileName = `explore-banner-${Date.now()}`;
      const image = `explore-banners/${fileName}.png`;
      const storageRef = ref(storage, image);
      await uploadBytes(storageRef, attachment);
      const banner = { image, url: draft.url || undefined };
      await apiCall("createExploreSettingsBanner")(banner);
      setSettings((state) => ({
        ...state,
        banners: state.banners.concat([banner]),
      }));
    },
    [apiCall, storage],
  );

  const removeBanner = useCallback(
    (index) => async () => {
      await apiCall("removeExploreSettingsBanner")(index);
      const clone = cloneDeep(settings);
      clone.banners.splice(index, 1);
      setSettings(clone);
    },
    [apiCall, settings],
  );

  const loadExploreSettings = useCallback(() => {
    api.getExploreSettings().then(setSettings);
  }, [api]);

  useEffect(() => {
    loadExploreSettings();
  }, [loadExploreSettings]);

  useEffect(() => {
    api.getCreators().then((result) => setCreators(result?.data));
  }, [api]);

  return (
    <MainLayout bg={bgColor} minH="100vh">
      <Box p={8}>
        {settings == null && (
          <Center mt={20}>
            <VStack spacing={6}>
              <CircularProgress isIndeterminate size={20} color="blue.400" thickness="4px" />
              <Text fontSize="xl" color="gray.500" fontWeight="medium">載入探索設定中...</Text>
            </VStack>
          </Center>
        )}

        {settings && (
          <Box maxW={1000} mx="auto">
            {/* Page Title */}
            <Box mb={12} textAlign="center">
              <Text fontSize="4xl" fontWeight="bold" color="blue.600" mb={2}>
                探索頁面管理
              </Text>
              <Text fontSize="lg" color="gray.500">
                管理橫幅、群組和項目排序
              </Text>
            </Box>

            {/* Banner Section */}
            <Card mb={12} shadow="2xl" rounded="3xl" overflow="hidden" bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody p={10}>
                <HStack mb={8} justify="space-between" align="center">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="3xl" fontWeight="bold" color="gray.300">
                      橫幅管理
                    </Text>
                    <Text color="gray.500" fontSize="md">
                      管理首頁輪播橫幅圖片
                    </Text>
                  </VStack>
                  <Badge colorScheme="blue" px={4} py={2} rounded="full" fontSize="md">
                    {settings?.banners?.length || 0} 個橫幅
                  </Badge>
                </HStack>

                <Box rounded="2xl" overflow="hidden" shadow="xl">
                  <Swiper
                    autoplay={{
                      delay: 5000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    pagination={{
                      clickable: true,
                    }}
                    navigation={true}
                    spaceBetween={0}
                    loop={settings?.banners?.length > 1}
                    modules={[Autoplay, Pagination, Navigation]}
                    style={{
                      borderRadius: "16px",
                    }}
                  >
                    {settings?.banners.map((banner, index) => (
                      <SwiperSlide key={`banners-${index}`}>
                        <Box position="relative" h="400px">
                          <a
                            href={banner.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Image
                              src={getPublicDownloadUrl(
                                typeof banner === "string" ? banner : banner.image,
                              )}
                              width="100%"
                              height="400px"
                              objectFit="cover"
                            />
                          </a>

                          <Tooltip label="刪除橫幅" placement="top">
                            <IconButton
                              icon={<FAIcon name="trash-alt" />}
                              onClick={removeBanner(index)}
                              colorScheme="red"
                              variant="solid"
                              isRound
                              position="absolute"
                              top={6}
                              right={6}
                              shadow="xl"
                              size="lg"
                              _hover={{ transform: "scale(1.1)" }}
                            />
                          </Tooltip>
                        </Box>
                      </SwiperSlide>
                    ))}
                    <SwiperSlide>
                      <AddBanner onAdd={addBanner} />
                    </SwiperSlide>
                  </Swiper>
                </Box>
              </CardBody>
            </Card>

            {/* Groups Section */}
            <Box mb={12}>
              <HStack mb={8} justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <Text fontSize="3xl" fontWeight="bold" color="gray.300">
                    探索群組
                  </Text>
                  <Text color="gray.500" fontSize="md">
                    使用拖放功能重新排序項目
                  </Text>
                </VStack>
                <Badge colorScheme="green" px={4} py={2} rounded="full" fontSize="md">
                  {settings.groups.length} 個群組
                </Badge>
              </HStack>

              {settings.groups.map((group, index) => (
                <Group
                  key={group.name}
                  {...group}
                  groupIndex={index}
                  onLoad={loadGroup(index)}
                  onRemove={removeGroup(index)}
                  onMove={moveGroup(index)}
                  onAddItem={openGroupEditModal(index)}
                  onSave={handleGroupSave}
                  onRegisterAddCallback={registerAddCallback}
                />
              ))}
              
              <Center mt={16}>
                <Button
                  onClick={createDraftGroup}
                  size="xl"
                  h={20}
                  px={12}
                  border="4px dashed"
                  borderColor="blue.300"
                  bg={addGroupBlueBg}
                  _hover={{
                    borderColor: "blue.500",
                    bg: addGroupBlueHoverBg,
                    transform: "scale(1.05)",
                  }}
                  transition="all 0.3s"
                  rounded="2xl"
                  leftIcon={<FAIcon name="plus-circle" fontSize="2xl" />}
                  fontSize="lg"
                  fontWeight="bold"
                  color="blue.600"
                  variant="ghost"
                >
                  新增探索群組
                </Button>
              </Center>
            </Box>
          </Box>
        )}

        {/* Status Indicator */}
        <Fade in={updateStatus !== "IDLE"} unmountOnExit>
          <Card
            position="fixed"
            bottom={8}
            right={8}
            shadow="2xl"
            bg={cardBg}
            borderWidth="2px"
            borderColor={borderColor}
            rounded="xl"
          >
            <CardBody p={6}>
              {updateStatus === "UPDATING" && (
                <HStack spacing={4}>
                  <Spinner size="md" color="blue.400" />
                  <Text fontWeight="semibold" fontSize="lg">更新中...</Text>
                </HStack>
              )}
              {updateStatus === "DONE" && (
                <HStack spacing={4} color="green.500">
                  <FAIcon name="check-circle" fontSize="xl" />
                  <Text fontWeight="semibold" fontSize="lg">已同步</Text>
                </HStack>
              )}
              {updateStatus === "ERROR" && (
                <HStack spacing={4} color="red.500">
                  <FAIcon name="exclamation-triangle" fontSize="xl" />
                  <Text fontWeight="semibold" fontSize="lg">同步失敗</Text>
                </HStack>
              )}
            </CardBody>
          </Card>
        </Fade>

        {/* Create Group Modal */}
        <Modal isOpen={!!draftGroup} onClose={() => setDraftGroup(null)} size="lg">
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg={cardBg} rounded="2xl" shadow="2xl" maxW="500px">
            <ModalBody p={0}>
              {draftGroup && (
                <Box p={10}>
                  <Text fontSize="3xl" fontWeight="bold" mb={10} textAlign="center" color="blue.600">
                    新增探索群組
                  </Text>
                  
                  <VStack spacing={8}>
                    <InputGroup size="lg">
                      <InputLeftAddon bg="blue.100" color="blue.700" fontWeight="bold" borderColor="blue.200">
                        <FAIcon name="tag" mr={2} />
                        名稱
                      </InputLeftAddon>
                      <Input
                        value={draftGroup.name}
                        onChange={(e) =>
                          setDraftGroup((state) => ({
                            ...state,
                            name: e.target.value,
                          }))
                        }
                        placeholder="輸入群組名稱"
                        borderColor="blue.200"
                        _focus={{ borderColor: "blue.400" }}
                      />
                    </InputGroup>
                    
                    <Box w="100%">
                      <Text mb={4} fontWeight="bold" color="gray.600" fontSize="lg">
                        選擇群組類型
                      </Text>
                      <RadioGroup
                        value={draftGroup.type}
                        onChange={(type) =>
                          setDraftGroup((state) => ({
                            ...state,
                            type,
                          }))
                        }
                      >
                        <HStack spacing={8} justify="center">
                          <Radio value="creators" colorScheme="purple" size="lg">
                            <VStack spacing={1} align="start">
                              <Text fontWeight="bold">創作者</Text>
                              <Text fontSize="sm" color="gray.500">顯示創作者資料</Text>
                            </VStack>
                          </Radio>
                          <Radio value="videos" colorScheme="orange" size="lg">
                            <VStack spacing={1} align="start">
                              <Text fontWeight="bold">影片</Text>
                              <Text fontSize="sm" color="gray.500">顯示影片內容</Text>
                            </VStack>
                          </Radio>
                        </HStack>
                      </RadioGroup>
                    </Box>
                    
                    <HStack spacing={4} w="100%">
                      <Button
                        onClick={() => setDraftGroup(null)}
                        variant="outline"
                        colorScheme="gray"
                        flex={1}
                        size="lg"
                      >
                        取消
                      </Button>
                      <Button
                        onClick={addGroup}
                        colorScheme="blue"
                        flex={2}
                        size="lg"
                        isDisabled={!draftGroup.name}
                        leftIcon={<FAIcon name="plus" />}
                        shadow="lg"
                        _hover={{ transform: "translateY(-2px)" }}
                      >
                        建立群組
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Item Selection Modal */}
        <Modal
          isOpen={editing != null}
          onClose={closeEditModal}
          size={isDesktop ? "4xl" : "full"}
        >
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg={cardBg} rounded="2xl" shadow="2xl" maxH="90vh">
            <ModalBody p={0} overflow="auto">
              {editingGroup.type === "creators" && (
                <Box p={8}>
                  <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center" color="purple.600">
                    選擇創作者
                  </Text>
                  <CreatorSelector creators={creators} onSelect={addItem} />
                </Box>
              )}
              {editingGroup.type === "videos" && (
                <Box p={8}>
                  <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center" color="orange.600">
                    選擇影片
                  </Text>
                  <VideoSelector onSelect={addItem} />
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </MainLayout>
  );
}
